import React from 'react';
import { elementStyleFrom, ISearchElementStyle, ISearchTextStyle } from './searchPageConfig';

// Consumer-side mirror of the admin editor's banner overlay config
// (ac-main/web: components/Storefront/Editor/bannerOverlayConfig.ts). Kept in sync so the
// storefront renders exactly what the "Customize Page" preview shows. Persisted as ONE
// opaque JSON string on IStorefrontBanner.overlayConfig. Style shapes are shared with the
// search page config (fontSize is a PERCENT number rendered as `${n}%`).

export type BannerOverlayHorizontal = 'left' | 'center' | 'right';
export type BannerOverlayVertical = 'top' | 'middle' | 'bottom';

export interface IBannerOverlayPosition {
    horizontal?: BannerOverlayHorizontal;   // default 'left'
    vertical?: BannerOverlayVertical;       // default 'middle'
    maxWidth?: number;                      // percent of banner width, default 50
    textAlign?: BannerOverlayHorizontal;    // default follows horizontal
}

export interface IBannerOverlayButton {
    label?: string;
    url?: string;                           // falls back to banner.linkUrl when empty
    style?: ISearchElementStyle;
    // Hover/focus highlight, merged OVER `style` while the pointer is on the button (and while it is
    // keyboard-focused), so it only needs the properties that change — a brighter backgroundColor, a
    // glow boxShadow, a borderColor. Absent ⇒ the color-agnostic default in index.css
    // (.banner-overlay-cta: brighten + lift + shadow).
    hoverStyle?: ISearchElementStyle;
}

export interface IBannerOverlayConfig {
    title?: string;
    titleStyle?: ISearchTextStyle;
    subtitle?: string;
    subtitleStyle?: ISearchTextStyle;
    button?: IBannerOverlayButton;
    position?: IBannerOverlayPosition;
    // Backdrop box around the whole text stack (title/subtitle/button): background, border,
    // radius, and padding (the extra space around the text).
    box?: ISearchElementStyle;
    // 0-100 opacity applied to the box's backgroundColor ONLY (combined into rgba at render
    // time — CSS `opacity` on the box would fade the text too). Absent = fully opaque.
    boxOpacity?: number;
}

// An overlay only renders when it has visible content (matches the admin's persistence rule).
export const hasBannerOverlayContent = (config: IBannerOverlayConfig): boolean =>
    !!(config.title?.trim() || config.subtitle?.trim() || config.button?.label?.trim());

// Safe parse: returns null on absence or malformed JSON, or when the parsed config has no
// visible content — so an untouched banner renders exactly as it does today.
export const parseBannerOverlayConfig = (raw?: string): IBannerOverlayConfig | null => {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as IBannerOverlayConfig;
        return hasBannerOverlayContent(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

// The overlay layer is a flex COLUMN, so vertical placement maps to justify-content (main
// axis) and horizontal placement maps to align-items (cross axis).
const horizontalJustify = (h?: BannerOverlayHorizontal): React.CSSProperties['alignItems'] =>
    h === 'center' ? 'center' : h === 'right' ? 'flex-end' : 'flex-start';

const verticalAlign = (v?: BannerOverlayVertical): React.CSSProperties['justifyContent'] =>
    v === 'top' ? 'flex-start' : v === 'bottom' ? 'flex-end' : 'center';

// The absolutely-positioned overlay layer that fills the banner. pointer-events:none lets
// clicks fall through to the banner image (its whole-banner linkUrl click-through stays),
// while the CTA button re-enables pointer events on itself.
export const overlayLayerStyleFrom = (position?: IBannerOverlayPosition): React.CSSProperties => ({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: verticalAlign(position?.vertical),
    alignItems: horizontalJustify(position?.horizontal),
    padding: '24px',
    boxSizing: 'border-box',
    pointerEvents: 'none',
});

// The bare absolute layer used when the banner is full-bleed: it only fills the banner and
// lets clicks fall through — the flex positioning lives on the page-frame below instead, so
// the overlay content stays aligned to the page content column rather than the viewport.
export const overlayFramedLayerStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
};

// The inner page-frame used with a full-bleed banner: constrains the overlay to the page
// content width and centers it (margin auto), then carries the same flex positioning the
// normal layer would — so title/subtitle/button place inside the page column exactly like a
// hero banner even though the banner image itself spans the whole viewport.
export const overlayFrameStyleFrom = (
    position: IBannerOverlayPosition | undefined,
    maxWidthPx: number,
): React.CSSProperties => ({
    width: '100%',
    height: '100%',
    maxWidth: `${maxWidthPx}px`,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: verticalAlign(position?.vertical),
    alignItems: horizontalJustify(position?.horizontal),
    padding: '24px',
    boxSizing: 'border-box',
    pointerEvents: 'none',
});

// Full-bleed overlay layer that SCALES with the banner instead of locking to a fixed pixel
// maxWidth. Everything is expressed in container-query width units (cqw = 1% of the banner
// container's width — the banner container sets container-type: inline-size), so the CTA's
// position AND size track the banner proportionally at every viewport: it grows on a 4K/wide
// screen and holds the same spot relative to the art. The insets place the content just inside
// the LCARS left column and above the bottom band, so a left/bottom-anchored CTA sits directly
// under the baked headline. `fontSize: 1cqw` is the base the button's percent font-size (and any
// em padding/shadow) scale from.
export const overlayScaledFrameStyle = (position?: IBannerOverlayPosition): React.CSSProperties => {
    const h = position?.horizontal ?? 'left';
    // A left-anchored CTA sits under a left headline (20cqw ~= the baked content x); center/right
    // use a small symmetric inset so the CTA centers under a centered headline.
    return {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: verticalAlign(position?.vertical ?? 'bottom'),
        alignItems: horizontalJustify(h),
        paddingLeft: h === 'left' ? '20cqw' : '4cqw',
        paddingRight: h === 'right' ? '20cqw' : '4cqw',
        paddingTop: '4cqw',
        paddingBottom: '2.6cqw',
        boxSizing: 'border-box',
        pointerEvents: 'none',
        fontSize: '1cqw',
    };
};

// Apply an opacity (0-100) to a solid CSS color. Handles #rgb/#rrggbb and rgb(...);
// anything else (named colors, existing rgba) is returned unchanged.
const withAlpha = (color: string, opacityPct: number): string => {
    const a = Math.max(0, Math.min(100, opacityPct)) / 100;
    const c = color.trim();
    const m3 = /^#([0-9a-f]{3})$/i.exec(c);
    if (m3) {
        const [r, g, b] = m3[1].split('').map((h) => parseInt(h + h, 16));
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
    const m6 = /^#([0-9a-f]{6})$/i.exec(c);
    if (m6) {
        const v = m6[1];
        return `rgba(${parseInt(v.slice(0, 2), 16)}, ${parseInt(v.slice(2, 4), 16)}, ${parseInt(v.slice(4, 6), 16)}, ${a})`;
    }
    const rgb = /^rgb\(([^)]+)\)$/i.exec(c);
    if (rgb) {
        // Support both comma- and space-separated channels (rgb(0,0,0) and the modern rgb(0 0 0)):
        // take the first three numbers, dropping any existing "/ alpha" component, then emit
        // comma-form rgba (universally valid). rgb(0 0 0) would otherwise yield invalid rgba(0 0 0, a).
        const parts = rgb[1].split('/')[0].trim().split(/[\s,]+/).filter(Boolean);
        if (parts.length >= 3) return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${a})`;
    }
    return c;
};

// CSS for the backdrop box on the overlay content column. boxOpacity folds into the
// backgroundColor as rgba so the text/button on top stay fully opaque.
export const overlayBoxStyleFrom = (config: IBannerOverlayConfig): React.CSSProperties => {
    const css = elementStyleFrom(config.box);
    if (config.boxOpacity != null && css.backgroundColor) {
        css.backgroundColor = withAlpha(String(css.backgroundColor), config.boxOpacity);
    }
    return css;
};

// The inner content column: constrains width (percent of the banner) and sets text alignment,
// defaulting to the horizontal placement.
export const overlayContentStyleFrom = (position?: IBannerOverlayPosition): React.CSSProperties => ({
    maxWidth: `${position?.maxWidth ?? 50}%`,
    textAlign: (position?.textAlign ?? position?.horizontal ?? 'left') as React.CSSProperties['textAlign'],
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: horizontalJustify(position?.horizontal),
});
